import React, { useMemo, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation, useParams } from 'react-router-dom';
import { useApolloClient } from '@apollo/client';
import type { Modifiers } from '@apollo/client/cache';
import { modifyAutoApplyInfo } from '../../../lib/apollo/client';
import { Box, Button, ButtonLink, Heading, Spinner, Text } from '@indeed/ifl-components';
import { Edit, FileDoc, QuickApply, DocReview } from '@indeed/ifl-icons';
import PageTransition from '../../../components/PageTransition/PageTransition';
import { AutoApplyHeader } from '../../../components/AutoApplyHeader/AutoApplyHeader';
import { ErrorDialog } from '../../../components/ErrorDialog/ErrorDialog';
import { ResumeValidationDialog } from '../components/ResumeValidationDialog';
import { usePageFlow } from '../../../lib/pageFlow/usePageFlow';
import { MatchPreferencesSection } from '../components/MatchPreferencesSection';
import { ResumePreviewSection } from './ResumePreviewSection';
import { CustomizeExperienceReview } from './CustomizeExperienceReview';
import { ReviewDisclaimer } from './ReviewDisclaimer';
import GettextFactory from '../../../util/GettextFactory';
import { jsProThemeButton } from '../components/jsProTheme/jsProTheme';
import { StickyActionFooter } from '../../../components/StickyActionFooter/StickyActionFooter';
import { useInitialState } from '../../../lib/context/InitialStateContext';
import { useIsProBrandingEnabled } from '../../../lib/hooks/useIsProBrandingEnabled';
import { useApplyForMeCopy } from '../hooks/useApplyForMeCopy';
import {
    useGetAutoApplyReviewDataQuery,
    useDownloadFileResumeForReviewMutation,
    useSetDqPrefillConsentMutation,
    useUpdateAutoApplyFromReviewMutation,
} from './__generated__/ReviewPage.generated';
import { useClearReviewBeforeSubmitDrafts } from './useClearReviewBeforeSubmitDrafts';
import { getContactInfo } from './contactInfo';
import { ContactInformationSection } from './ContactInformationSection';
import type { AutoApplyMode } from '../../../__generated__/graphql-base-types';
import { useProctorGroups } from '@indeed/frontend-proctor-utils';
import { JSMABridge } from '@indeed/jsma-js-bridge';
import { useEventLogger } from '../../../lib/eventLogger/EventLoggerProvider';
import { mapLatestApplyResumeType } from '../resumePrefill';
import type { UpdateAutoApplyInput } from '../../../__generated__/graphql-base-types';
import { clearProfileModified, hasProfileModified } from '../AutoApplySetupSessionState';
import { AutoApplySetupRumContext, logAutoApplySetupRumError } from '../autoApplySetupRumErrors';
import { AutoApplySetupAction, type AutoApplySetupReviewAction } from '../autoApplySetupActions';

// Fields owned by the Review page's primary action. Both Activate and Update build
// an input from this set; the shared submit pipeline ITV-checks and caches exactly
// the fields that were provided.
const MANAGED_FIELDS = [
    'autoApplyStatus',
    'selectedResumeType',
    'sqPastAnswerPrefillPreference',
    'mode',
] as const;

/**
 * Review Page for Auto Apply Setup
 * Shows review sections for job preferences and resume before activation
 *
 * Figma Design: Review details (Nov)
 * - Indeed Resume: https://www.figma.com/design/pXXAQfQFDZqsF1BxMo5ccB/-CS--Assisted-apply-2.0--Auto-Apply-?node-id=6716-29122
 * - File Resume: https://www.figma.com/design/pXXAQfQFDZqsF1BxMo5ccB/-CS--Assisted-apply-2.0--Auto-Apply-?node-id=6716-41064
 */
const ReviewPage: React.FC = () => {
    const { goForward, goToStep, enterSubFlow, loading: pageFlowLoading } = usePageFlow();
    const apolloClient = useApolloClient();
    const gt = GettextFactory();
    const location = useLocation();
    const { reviewVariant } = useParams<{ reviewVariant?: string }>();
    const { locale, returnUrl, isJobSearchApp } = useInitialState();
    const eventLogger = useEventLogger();
    const isProBrandingEnabled = useIsProBrandingEnabled();
    const copy = useApplyForMeCopy();

    // State for PDF URL cache
    const [pdfUrl, setPdfUrl] = useState<string | null>(null);
    const [loadingPdf, setLoadingPdf] = useState(false);
    // One dialog per recovery scenario, so "Try again" maps to the right action
    // without tracking an error source. A failed initial data fetch refetches;
    // a failed submit (Apollo error *or* an ITV mismatch where the mutation
    // succeeds but echoes back the wrong value) re-runs the Activate/Update.
    const [showDataErrorDialog, setShowDataErrorDialog] = useState(false);
    const [showSubmitErrorDialog, setShowSubmitErrorDialog] = useState(false);
    const [showResumeValidationDialog, setShowResumeValidationDialog] = useState(false);
    const [termsAccepted, setTermsAccepted] = useState(false);

    // Batch cleanup of stale Review-Before-Submit drafts after a progressive-automation
    // preference update (PSUB-4840). Apollo-mutation-like API: the action clears the
    // drafts and `clearingReviewBeforeSubmitDrafts` stays true for the whole operation.
    const [clearReviewBeforeSubmitDrafts, { loading: clearingReviewBeforeSubmitDrafts }] =
        useClearReviewBeforeSubmitDrafts();

    const { getGroup } = useProctorGroups();
    const isApplyForMeDisabled = getGroup('apply_for_me_sfm_tog')?.value === 0;
    const isM1Test = getGroup('ia_auto_apply_preference_m1_tst')?.value === 1;
    const isAutomationLevelEnabled =
        !isApplyForMeDisabled && getGroup('auto_apply_main_tog')?.value === 1;
    const sqPrefillEnabled = getGroup('js_pro_auto_apply_sq_prefill_tst')?.value === 1;
    const isContactInfoEnabled = getGroup('auto_apply_setup_review_contact_info')?.value === 1;
    // Progressive-automation bucket (rnhp_auto_apply_banner_tst === 2): drives the
    // "Customize your experience" Review variant (IAFE-2080) and the latest-apply
    // resume pre-fill for banner entries with no explicit setup choice (IAFE-2079).
    const isProgressiveAutomation = getGroup('rnhp_auto_apply_banner_tst')?.value === 2;
    // Only evaluate the DQ experiment for users who can reach its progressive-
    // automation control, avoiding exposure from legacy Review-page traffic.
    const dqPrefillEnabled =
        isProgressiveAutomation && getGroup('ia_dq_questions_prefill_tst')?.value === 1;

    // Review entry variant (IAFE-2081). Under progressive automation, Review is served
    // at /review/apply-for-me (MyJobs/promo) or /review/review-before-submit (homepage).
    // The disabled treatment normalizes every route to RBS behavior; otherwise the path
    // segment selects the variant and a bare /review keeps the legacy behavior.
    const isApplyForMeVariant = !isApplyForMeDisabled && reviewVariant === 'apply-for-me';
    const isReviewBeforeSubmitVariant =
        isApplyForMeDisabled || reviewVariant === 'review-before-submit';

    // Carry the current Review variant into the resume edit detour so it can be
    // read back on exit and return to the matching variant, even after the
    // external profile-editor round-trip drops the recorded origin (IAFE-2551).
    // Only append an effective known variant. This also normalizes an Apply For Me deep
    // link to RBS in the disabled treatment before entering the resume edit detour.
    const resumeEditVariant = isApplyForMeVariant
        ? 'apply-for-me'
        : isReviewBeforeSubmitVariant
          ? 'review-before-submit'
          : undefined;
    const resumeEditRoute = resumeEditVariant
        ? `/auto-apply-setup/resume/edit?reviewVariant=${resumeEditVariant}`
        : '/auto-apply-setup/resume/edit';

    // Fetch resume data from GraphQL
    const {
        data,
        loading,
        error: queryError,
        refetch,
    } = useGetAutoApplyReviewDataQuery({
        errorPolicy: 'all',
        fetchPolicy: 'cache-first',
    });

    // The resume type to use: the user's saved setup choice, falling back to
    // their latest-apply resume type under the progressive-automation bucket
    // (IAFE-2079). Drives the preview and activation validation so a banner user
    // who lands straight on Review sees and activates the correct resume. See
    // mapLatestApplyResumeType for the latest-apply → setup mapping (e.g.
    // TAILORED_RESUME → Indeed Resume).
    const latestApplyResumeType = data?.jobSeekerProfile?.profile?.latestApply?.resumeType;
    const selectedResumeType =
        data?.autoApplyInfo?.selectedResumeType ??
        (isProgressiveAutomation ? mapLatestApplyResumeType(latestApplyResumeType) : undefined);

    // Mutation to fetch PDF URL
    const [downloadFileResume] = useDownloadFileResumeForReviewMutation();

    // Mutation to activate Auto Apply and to persist Review-page preferences.
    const [updateAutoApply, { loading: updating }] = useUpdateAutoApplyFromReviewMutation();
    // Account-level DQ consent shared with Smart Apply. Unlike the SQ preference,
    // this is not owned by autoApplyInfo and must not be sent through updateAutoApply.
    const [setDqPrefillConsent, { loading: updatingDqConsent }] = useSetDqPrefillConsentMutation();

    // Progressive-automation variant (rnhp_auto_apply_banner_tst === 2) toggle state.
    // The SQ and DQ toggles are local until the user clicks Update, which persists
    // SQ through updateAutoApply and DQ through setDqPrefillConsent. They are initialized
    // from their respective sources of truth (SQ defaults ON; missing DQ consent defaults
    // OFF per legal requirement) and re-seeded once the query resolves.
    const [sqOptedIn, setSqOptedIn] = useState(
        () => data?.autoApplyInfo?.sqPastAnswerPrefillPreference !== 'OPT_OUT',
    );
    const persistedDqOptedIn = data?.eligiblePastDQAnswers?.consent === true;
    const [dqOptedIn, setDqOptedIn] = useState(() => persistedDqOptedIn);
    useEffect(() => {
        setSqOptedIn(data?.autoApplyInfo?.sqPastAnswerPrefillPreference !== 'OPT_OUT');
    }, [data?.autoApplyInfo?.sqPastAnswerPrefillPreference]);
    useEffect(() => {
        setDqOptedIn(persistedDqOptedIn);
    }, [persistedDqOptedIn]);

    // "Active Apply For Me" (IAFE-2607): within the available Apply For Me variant,
    // only ENABLED + APPLY_FOR_ME is treated as already activated. Other states see
    // Activate with unchecked consent. The disabled treatment never selects this variant.
    const isActiveApplyForMe =
        data?.autoApplyInfo?.autoApplyStatus === 'ENABLED' &&
        data?.autoApplyInfo?.mode === 'APPLY_FOR_ME';

    // Pre-check the apply-for-me consent box only for an already-active Apply For Me
    // user (IAFE-2081/2607): they consented when they first enrolled, so an
    // already-activated editor isn't re-gated on "Update". Runs once the initial
    // fetch resolves; `autoApplyInfo` isn't mutated in-cache while on this page (only
    // at submit time, which navigates away), so this won't fight a manual uncheck.
    useEffect(() => {
        if (isApplyForMeVariant && isActiveApplyForMe) {
            setTermsAccepted(true);
        }
    }, [isActiveApplyForMe, isApplyForMeVariant]);

    // Surface the data-error dialog for a failed initial data fetch. Mutation
    // failures are handled inside submitReview via the submit-error dialog, so
    // they are intentionally not driven from updateError here.
    useEffect(() => {
        if (queryError) {
            logAutoApplySetupRumError(queryError, AutoApplySetupRumContext.ReviewLoad);
            setShowDataErrorDialog(true);
        }
    }, [queryError]);

    // Shared submit pipeline for the Review page's primary action (Activate or
    // Update). It validates a resume exists, folds in the progressive-automation
    // customize edits (shared by both actions), persists each setting through its
    // owning mutation, ITV-checks the returned values, and updates the Auto Apply
    // cache. The two callers differ only in their action-specific input, their success
    // outcome (advance vs. return), and their error logging.
    const submitReview = async ({
        action,
        input,
        onSuccess,
        onError,
    }: {
        action: AutoApplySetupReviewAction;
        input: UpdateAutoApplyInput;
        onSuccess: () => void | Promise<void>;
        onError: (err: unknown) => void;
    }): Promise<void> => {
        setShowSubmitErrorDialog(false);

        // Validate that a resume exists before persisting anything. selectedResumeType
        // is the outer computed value, which falls back to the latest-apply resume for
        // progressive-automation banner entries with no saved choice (IAFE-2079).
        const hasIndeedResume = selectedResumeType === 'INDEED_RESUME' && indeedResume;
        const hasFileResume =
            selectedResumeType === 'SAVED_FILE_RESUME' && selectedFileResume != null;

        if (!hasIndeedResume && !hasFileResume) {
            setShowResumeValidationDialog(true);
            return;
        }

        // Resume confirmation is logged on both Activate and Update once a valid
        // resume has been confirmed.
        eventLogger.logAutoApplySetupResumeConfirmationEvent?.({
            resumeType: selectedResumeType ?? '',
        });

        // Progressive automation always persists its Auto Apply-owned customize-screen
        // edits — Activate and Update share that screen, so both carry the SQ toggle plus
        // the pre-filled resume type. DQ consent is persisted separately below. The resume
        // is sent only when it was pre-filled
        // (no saved choice) AND the mapped value differs from the raw latest-apply type
        // (e.g. TAILORED_RESUME → INDEED_RESUME); when they match the backend already
        // defaults to the same resume, so there is nothing to persist (IAFE-2079/2080).
        if (isProgressiveAutomation) {
            input.sqPastAnswerPrefillPreference = sqOptedIn ? 'OPT_IN' : 'OPT_OUT';
            if (
                data?.autoApplyInfo?.selectedResumeType == null &&
                selectedResumeType != null &&
                selectedResumeType !== latestApplyResumeType
            ) {
                input.selectedResumeType = selectedResumeType;
            }
        }

        const hasChanges = MANAGED_FIELDS.some((field) => input[field] !== undefined);
        let rumContext = AutoApplySetupRumContext.ReviewSubmit(action);

        try {
            // Only call the mutation when there is something to persist.
            if (hasChanges) {
                const result = await updateAutoApply({ variables: { input } });
                const updated = result.data?.updateAutoApply;

                // ITV check: every field we sent must be echoed back unchanged.
                if (
                    !updated ||
                    MANAGED_FIELDS.some(
                        (field) => input[field] !== undefined && updated[field] !== input[field],
                    )
                ) {
                    logAutoApplySetupRumError(
                        new Error('Auto Apply Review update failed intent verification'),
                        AutoApplySetupRumContext.ReviewItv(action),
                        true,
                    );
                    setShowSubmitErrorDialog(true);
                    return;
                }

                // Update cache only for the persisted fields, after ITV passes.
                const cacheModifiers: Modifiers = {};
                for (const field of MANAGED_FIELDS) {
                    if (input[field] !== undefined) {
                        cacheModifiers[field] = () => updated[field];
                    }
                }
                modifyAutoApplyInfo(apolloClient.cache, cacheModifiers);
            }

            // Only persist changed DQ consent when its test bucket exposes the preference.
            // Missing consent is rendered as opt-out, so leaving that toggle untouched does
            // not create a false consent record or purge answers the user did not review.
            if (dqPrefillEnabled && persistedDqOptedIn !== dqOptedIn) {
                rumContext = AutoApplySetupRumContext.ReviewDqConsentSave(action);
                const result = await setDqPrefillConsent({
                    variables: { consent: dqOptedIn },
                });
                if (result.data?.setDqPrefillConsent?.consent !== dqOptedIn) {
                    logAutoApplySetupRumError(
                        new Error('Auto Apply Review DQ consent failed intent verification'),
                        AutoApplySetupRumContext.ReviewDqConsentItv(action),
                        true,
                    );
                    setShowSubmitErrorDialog(true);
                    return;
                }
            }

            rumContext = AutoApplySetupRumContext.ReviewSubmit(action);
            await onSuccess();
        } catch (err) {
            onError(err);
            logAutoApplySetupRumError(err, rumContext);
            setShowSubmitErrorDialog(true);
        }
    };

    // Activate Auto Apply: enable it, plus the legacy first-activation SQ opt-in
    // default and any entry-variant mode switch, then verify/cache and advance to the
    // next step. The progressive customize edits are applied by submitReview.
    const handleActivate = () => {
        eventLogger.logAutoApplySetupButtonClickEvent?.({
            currentPage: location.pathname,
            button: AutoApplySetupAction.Activate,
        });
        const input: UpdateAutoApplyInput = { autoApplyStatus: 'ENABLED' };
        // Legacy first activation (non-progressive): persist the SQ opt-in default for
        // new users. Under progressive automation the SQ toggle is persisted by
        // submitReview instead, so this branch is skipped.
        if (
            !isProgressiveAutomation &&
            sqPrefillEnabled &&
            data?.autoApplyInfo?.sqPastAnswerPrefillPreference == null
        ) {
            input.sqPastAnswerPrefillPreference = 'OPT_IN';
        }
        // Persist a mode switch when an available entry variant changes the automation
        // mode (IAFE-2081). The disabled treatment makes RBS the effective variant, so
        // this path cannot write APPLY_FOR_ME. The shared pipeline ITV-checks and caches
        // `mode` like any other managed field.
        const targetMode: AutoApplyMode | undefined = isApplyForMeVariant
            ? 'APPLY_FOR_ME'
            : isReviewBeforeSubmitVariant
              ? 'REVIEW_BEFORE_SUBMIT'
              : undefined;
        if (targetMode != null && targetMode !== data?.autoApplyInfo?.mode) {
            input.mode = targetMode;
        }
        return submitReview({
            action: AutoApplySetupAction.Activate,
            input,
            onSuccess: () => goForward(),
            onError: (err) => console.error('Failed to activate Auto Apply:', err),
        });
    };

    const handleEditResume = () => {
        eventLogger.logAutoApplySetupButtonClickEvent?.({
            currentPage: location.pathname,
            button: 'edit-resume',
        });
        // Enter the resume edit sub-flow so exiting pops back to Review (IAFE-383).
        // Carry the current variant in the URL so the detour can return to the same
        // variant even after the external profile-editor round-trip drops the
        // recorded origin — the param survives the round-trip in the `continue`
        // URL and is read back on exit (IAFE-2551).
        void enterSubFlow(resumeEditRoute);
    };

    const handleEditApplicationQuestions = () => {
        eventLogger.logAutoApplySetupButtonClickEvent?.({
            currentPage: location.pathname,
            button: 'edit-application-questions',
        });
        void enterSubFlow(resumeEditRoute);
    };

    const handleEditAutomationLevel = () => {
        eventLogger.logAutoApplySetupButtonClickEvent?.({
            currentPage: location.pathname,
            button: 'edit-automation-level',
        });
        void enterSubFlow('/auto-apply-setup/automation-level/edit');
    };

    const handleResumeValidationOk = () => {
        eventLogger.logAutoApplySetupButtonClickEvent?.({
            currentPage: location.pathname,
            button: 'resume-validation-modal-ok',
        });
        // Navigate to resume page
        void goToStep('/auto-apply-setup/resume');
    };

    const navigateToReturn = async () => {
        if (isJobSearchApp) {
            try {
                await JSMABridge.dismissModalWithWebView({});
                return;
            } catch {
                // Fallback for older app versions where dismissModalWithWebView is unavailable
            }
        }
        window.location.href = returnUrl;
    };

    const shouldClearReviewBeforeSubmitDrafts = (): boolean =>
        isProgressiveAutomation && isReviewBeforeSubmitVariant && hasProfileModified();

    // Update: no action-specific input — submitReview persists the progressive
    // customize edits and then returns to the caller. In the legacy (non-progressive)
    // flow there is nothing to persist, so the pipeline skips the mutation and simply
    // returns.
    const handleUpdate = () => {
        eventLogger.logAutoApplySetupButtonClickEvent?.({
            currentPage: location.pathname,
            button: AutoApplySetupAction.Update,
        });
        const input: UpdateAutoApplyInput = {};
        return submitReview({
            action: AutoApplySetupAction.Update,
            input,
            onSuccess: async () => {
                if (shouldClearReviewBeforeSubmitDrafts()) {
                    try {
                        await clearReviewBeforeSubmitDrafts();
                        clearProfileModified();
                    } catch (err) {
                        console.error('Failed to clear Review Before Submit drafts:', err);
                    }
                }
                await navigateToReturn();
            },
            onError: (err) => console.error('Failed to update Auto Apply preferences:', err),
        });
    };

    // Extract resume data
    const indeedResume = useMemo(() => {
        return data?.jobSeekerProfile?.profile?.resume.find(
            (resume) => resume.resumeType === 'INDEED_RESUME',
        );
    }, [data?.jobSeekerProfile?.profile?.resume]);

    const contactInfo = useMemo(
        () =>
            getContactInfo(
                data?.jobSeekerProfile?.profile,
                data?.currentUser?.account?.accountProfile,
                isContactInfoEnabled,
                gt,
            ),
        [data, isContactInfoEnabled, gt],
    );

    // Dual-resume migration (PROF-9839): when enabled, read uploaded file
    // resumes from the new `jobSeekerResumes` platform and use the embedded
    // `pdfResumeFile.url` for the preview; otherwise keep the legacy
    // `fileInformation` source and the `DownloadFileResumeForReview` mutation.
    const isDualResumesEnabled = data?.jobSeekerProfileDualResumesEnabled?.enabled === true;

    // V2 file resume + embedded PDF URL.
    const v2SelectedFileResume = useMemo(() => {
        const bundles = data?.jobSeekerResumes?.resumes ?? [];
        for (const bundle of bundles) {
            const metadata = bundle.originalUploadedFile?.metadata;
            if (metadata && metadata.fileType === 'RESUME') {
                return {
                    id: metadata.id,
                    fileName: metadata.fileName ?? null,
                    uploadTimestamp: metadata.uploadTimestamp ?? null,
                    pdfUrl: bundle.pdfResumeFile?.url ?? null,
                };
            }
        }
        return null;
    }, [data?.jobSeekerResumes?.resumes]);

    const legacyFileResumes = useMemo(() => {
        return (
            data?.jobSeekerProfile?.profile?.fileInformation?.filter(
                (file) => file.fileType === 'RESUME',
            ) || []
        );
    }, [data?.jobSeekerProfile?.profile?.fileInformation]);

    const selectedFileResume = isDualResumesEnabled
        ? v2SelectedFileResume
        : legacyFileResumes.length > 0
          ? legacyFileResumes[0]
          : null;

    const showIndeedResume = indeedResume && selectedResumeType === 'INDEED_RESUME';
    // Show (and fetch) the file resume only when it is the actual selection —
    // not merely "not Indeed", which would surface a file even when the
    // selection is INDEED_RESUME without an Indeed resume present, or unset.
    const showFileResume = selectedResumeType === 'SAVED_FILE_RESUME' && selectedFileResume != null;

    // Hide Continue button if Auto Apply is already enabled
    const autoApplyStatus = data?.autoApplyInfo?.autoApplyStatus;
    const isAutoApplyDisabled = autoApplyStatus !== 'ENABLED';

    // Progressive automation is always a manage/edit screen for an already-enrolled user.
    // Per IAFE-2607 the available Apply For Me variant uses "Update" only for an active
    // user (ENABLED + APPLY_FOR_ME); its other states use "Activate". RBS and the disabled
    // treatment use "Update". This also drives failed-submit retry behavior.
    const isProgressiveActivateAction = isApplyForMeVariant && !isActiveApplyForMe;

    // Adopt the embedded V2 PDF URL the moment the query resolves.
    useEffect(() => {
        if (!isDualResumesEnabled) return;
        if (!showFileResume) return;
        if (!v2SelectedFileResume) return;
        setPdfUrl(v2SelectedFileResume.pdfUrl);
    }, [isDualResumesEnabled, showFileResume, v2SelectedFileResume]);

    // Fetch PDF URL when file resume is present (legacy path only).
    useEffect(() => {
        if (isDualResumesEnabled) return;
        const fetchPdfUrl = async () => {
            if (showFileResume && selectedFileResume?.id && !pdfUrl && !loadingPdf) {
                setLoadingPdf(true);
                try {
                    const result = await downloadFileResume({
                        variables: { fileId: selectedFileResume.id },
                    });
                    const url =
                        result.data?.createJobSeekerProfileResumeFileTempUrls?.pdfResumeUrl || null;
                    setPdfUrl(url);
                } catch (error) {
                    console.error('Failed to fetch PDF URL:', error);
                    setPdfUrl(null);
                } finally {
                    setLoadingPdf(false);
                }
            }
        };

        void fetchPdfUrl();
    }, [
        isDualResumesEnabled,
        selectedFileResume?.id,
        showFileResume,
        pdfUrl,
        loadingPdf,
        downloadFileResume,
    ]);

    const helmet = (
        <Helmet>
            <title>{copy.reviewTitle}</title>
            <meta name="description" content={copy.reviewApplicationsMayChange} />
        </Helmet>
    );
    // Recovery for a failed initial data fetch: refetch.
    const dataErrorDialog = (
        <ErrorDialog
            isOpen={showDataErrorDialog}
            onOpenChange={setShowDataErrorDialog}
            onAction={async () => {
                eventLogger.logAutoApplySetupButtonClickEvent?.({
                    currentPage: location.pathname,
                    button: 'try-again',
                });
                await refetch();
            }}
        />
    );

    // Recovery for a failed submit: re-run whichever primary action was shown. The
    // mutation is shared by Activate and Update — Activate is shown in the legacy flow
    // while Auto Apply is disabled, and in the progressive-automation apply-for-me mode
    // switch; everything else is an Update.
    const shouldRetryActivate = isProgressiveAutomation
        ? isProgressiveActivateAction
        : isAutoApplyDisabled;
    const retrySubmit = shouldRetryActivate
        ? async () => {
              eventLogger.logAutoApplySetupButtonClickEvent?.({
                  currentPage: location.pathname,
                  button: 'activate-try-again',
              });
              await handleActivate();
          }
        : async () => {
              eventLogger.logAutoApplySetupButtonClickEvent?.({
                  currentPage: location.pathname,
                  button: 'update-try-again',
              });
              await handleUpdate();
          };
    const submitErrorDialog = (
        <ErrorDialog
            isOpen={showSubmitErrorDialog}
            onOpenChange={setShowSubmitErrorDialog}
            onAction={retrySubmit}
        />
    );

    if (loading || !data) {
        return (
            <PageTransition pageKey="auto-apply-preference">
                {helmet}
                <Box sx={{ padding: 4, textAlign: 'center' }}>
                    <Spinner title={gt.gettext('Loading')} />
                </Box>
                {dataErrorDialog}
                {submitErrorDialog}
            </PageTransition>
        );
    }

    // Progressive automation is a "Customize your experience" manage/edit screen
    // (IAFE-2080). When Apply For Me is available, its route layers on consent and the
    // Activate action (IAFE-2081); the disabled treatment remains the RBS update screen.
    if (isProgressiveAutomation) {
        return (
            <>
                <PageTransition pageKey="auto-apply-preference">
                    {helmet}
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5, flex: 1 }}>
                            <CustomizeExperienceReview
                                contactInfo={contactInfo}
                                showIndeedResume={!!showIndeedResume}
                                showFileResume={showFileResume}
                                indeedResume={indeedResume}
                                selectedFileResume={selectedFileResume}
                                pdfUrl={pdfUrl}
                                loadingPdf={loadingPdf}
                                locale={locale}
                                onEditResume={handleEditResume}
                                sqOptedIn={sqOptedIn}
                                onSqOptInChange={setSqOptedIn}
                                dqOptedIn={dqOptedIn}
                                onDqOptInChange={setDqOptedIn}
                                showDqToggle={dqPrefillEnabled}
                            />

                            {/* Terms + consent for the apply-for-me variant (IAFE-2081).
                                Progressive automation always shows the Apply For Me (Beta)
                                heading and its single-block terms, independent of the
                                rebranding and automation-level proctors. */}
                            {isApplyForMeVariant && (
                                <ReviewDisclaimer
                                    title={copy.progressiveAutomationTermsHeading}
                                    termsBody={copy.progressiveAutomationReviewTerms}
                                    showConsent
                                    consentLabel={copy.reviewCheckboxLabel}
                                    checked={termsAccepted}
                                    onCheckedChange={setTermsAccepted}
                                />
                            )}
                        </Box>
                    </Box>
                    {dataErrorDialog}
                    {submitErrorDialog}
                    <ResumeValidationDialog
                        isOpen={showResumeValidationDialog}
                        onOpenChange={setShowResumeValidationDialog}
                        onOk={handleResumeValidationOk}
                    />
                </PageTransition>
                {/* Primary action floats at the bottom (this branch only renders
                    for the progressive-automation bucket). */}
                <StickyActionFooter maxInlineSize="25.125rem">
                    <Button
                        onClick={
                            isProgressiveActivateAction ? handleActivate : () => void handleUpdate()
                        }
                        sx={isProBrandingEnabled ? jsProThemeButton.primary : {}}
                        full
                        loading={updating || updatingDqConsent || pageFlowLoading}
                        // Consent gates proceeding while the apply-for-me checkbox is shown.
                        disabled={
                            updating ||
                            updatingDqConsent ||
                            pageFlowLoading ||
                            clearingReviewBeforeSubmitDrafts ||
                            (isApplyForMeVariant && !termsAccepted)
                        }
                    >
                        {isProgressiveActivateAction ? copy.activateProduct : gt.gettext('Update')}
                    </Button>
                </StickyActionFooter>
            </>
        );
    }

    return (
        <PageTransition pageKey="auto-apply-preference">
            {helmet}

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 5,
                        flex: 1,
                    }}
                >
                    <AutoApplyHeader
                        title={gt.gettext('Review details')}
                        description={copy.reviewApplicationsMayChange}
                    />

                    {/* Review Sections Container */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 5,
                        }}
                    >
                        {/* Level of Automation is editable only when its Proctor is enabled
                            and Apply For Me remains available. */}
                        {isAutomationLevelEnabled && data?.autoApplyInfo?.mode && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Heading level={3} as="h2">
                                        {gt.gettext('Level of automation')}
                                    </Heading>
                                    <Button
                                        onClick={handleEditAutomationLevel}
                                        aria-label={gt.gettext('Edit level of automation')}
                                        variant="tertiary"
                                        iconOnly
                                    >
                                        <Edit size="md" variant="stroke" aria-hidden />
                                    </Button>
                                </Box>
                                <Box
                                    sx={{
                                        border: '1px solid',
                                        borderColor: 'border.weak',
                                        borderRadius: 'lg',
                                        padding: 4,
                                        display: 'flex',
                                        gap: 2,
                                        alignItems: 'flex-start',
                                    }}
                                >
                                    <Box sx={{ flexShrink: 0 }}>
                                        {data.autoApplyInfo.mode === 'APPLY_FOR_ME' ? (
                                            <QuickApply size="md" aria-hidden />
                                        ) : (
                                            <DocReview size="md" aria-hidden />
                                        )}
                                    </Box>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 2,
                                        }}
                                    >
                                        <Text sx={{ fontWeight: '700' }}>
                                            {data.autoApplyInfo.mode === 'APPLY_FOR_ME'
                                                ? copy.applyForMeModeTitle
                                                : gt.gettext('Review before submit')}
                                        </Text>
                                        <Text level={2} variant="secondary">
                                            {data.autoApplyInfo.mode === 'APPLY_FOR_ME'
                                                ? gt.gettext(
                                                      "We'll prepare and submit applications to jobs that match your preferences.",
                                                  )
                                                : gt.gettext(
                                                      "We'll prepare applications to relevant jobs for you to review and submit.",
                                                  )}
                                        </Text>
                                    </Box>
                                </Box>
                            </Box>
                        )}

                        {/* Match Preferences Section - only shown when M1 proctor is enabled */}
                        {isM1Test && <MatchPreferencesSection />}

                        {/* Resume Section */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <ResumePreviewSection
                                heading={
                                    sqPrefillEnabled
                                        ? gt.gettext('How we autofill applications')
                                        : gt.gettext('Resume')
                                }
                                onEditResume={handleEditResume}
                                showIndeedResume={!!showIndeedResume}
                                showFileResume={showFileResume}
                                indeedResume={indeedResume}
                                selectedFileResume={selectedFileResume}
                                pdfUrl={pdfUrl}
                                loadingPdf={loadingPdf}
                                locale={locale}
                            />

                            {/* Application Questions Card - Only shown when sqPrefillEnabled is true */}
                            {sqPrefillEnabled && (
                                <ButtonLink
                                    href="/auto-apply-setup/resume/edit"
                                    onClick={(e: React.MouseEvent) => {
                                        e.preventDefault();
                                        handleEditApplicationQuestions();
                                    }}
                                    variant="tertiary"
                                    sx={{
                                        border: '1px solid',
                                        borderColor: 'border.weak',
                                        borderRadius: 'lg',
                                        padding: 4,
                                        marginBlockStart: 2,
                                        display: 'flex',
                                        gap: 2,
                                        alignItems: 'flex-start',
                                        textDecoration: 'none',
                                        color: 'inherit',
                                        inlineSize: '100%',
                                        blockSize: 'auto',
                                        fontWeight: 'normal',
                                        justifyContent: 'flex-start',
                                    }}
                                >
                                    <Box sx={{ flexShrink: 0 }}>
                                        <FileDoc size="md" aria-hidden />
                                    </Box>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 1,
                                        }}
                                    >
                                        <Heading level={3} as="h3" sx={{ marginBlockEnd: 0 }}>
                                            {gt.gettext('Application questions')}
                                        </Heading>
                                        <Text level={2} variant="secondary">
                                            {data?.autoApplyInfo?.sqPastAnswerPrefillPreference !==
                                            'OPT_OUT'
                                                ? gt.gettext(
                                                      'Use AI to answer application questions',
                                                  )
                                                : gt.gettext(
                                                      'Do not use AI to answer application questions',
                                                  )}
                                        </Text>
                                    </Box>
                                </ButtonLink>
                            )}
                        </Box>

                        {/* Contact Information Section */}
                        {contactInfo.showContactInfo && (
                            <ContactInformationSection contactInfo={contactInfo} />
                        )}
                    </Box>

                    {/* Auto Apply (Beta) Terms */}
                    <ReviewDisclaimer
                        title={copy.betaTermsHeading}
                        termsBody={
                            isAutomationLevelEnabled
                                ? copy.reviewTermsAutomationEnabled
                                : copy.reviewTermsAutomationDisabled
                        }
                        showConsent={isAutoApplyDisabled}
                        consentLabel={copy.reviewCheckboxLabel}
                        checked={termsAccepted}
                        onCheckedChange={setTermsAccepted}
                    />

                    {/* Action Button */}
                    <Box sx={{ marginBlockStart: 'auto' }}>
                        {isAutoApplyDisabled ? (
                            <Button
                                onClick={handleActivate}
                                sx={isProBrandingEnabled ? jsProThemeButton.primary : {}}
                                full
                                loading={updating || pageFlowLoading}
                                disabled={updating || pageFlowLoading || !termsAccepted}
                            >
                                {copy.activateProduct}
                            </Button>
                        ) : (
                            <Button
                                onClick={handleUpdate}
                                sx={isProBrandingEnabled ? jsProThemeButton.primary : {}}
                                full
                                loading={
                                    updating || pageFlowLoading || clearingReviewBeforeSubmitDrafts
                                }
                                disabled={
                                    updating || pageFlowLoading || clearingReviewBeforeSubmitDrafts
                                }
                            >
                                {gt.gettext('Update')}
                            </Button>
                        )}
                    </Box>
                </Box>
            </Box>
            {dataErrorDialog}
            {submitErrorDialog}
            <ResumeValidationDialog
                isOpen={showResumeValidationDialog}
                onOpenChange={setShowResumeValidationDialog}
                onOk={handleResumeValidationOk}
            />
        </PageTransition>
    );
};

export default ReviewPage;
